import { useEffect, useRef, useState } from "react";
import { MessageSquare, X, Send, Loader2 } from "lucide-react";
import axios from "axios";
import { track } from "../lib/track";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "zd_chat_session";

const WELCOME =
  "Hi — I'm Zarco's Decks & Outdoor Living's assistant. Tell me about the project you're thinking about (deck, porch, stairs, screened room) and I'll help you sort out options. When you're ready, I can pass your details straight to Orlando for a free estimate.";

function getSessionId() {
  let sid = localStorage.getItem(STORAGE_KEY);
  if (!sid) {
    sid = `web-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem(STORAGE_KEY, sid);
  }
  return sid;
}

/** Minimal markdown-lite for chat bubbles: **bold** → <strong>, line breaks preserved by CSS. */
function renderBubble(text) {
  return String(text)
    .split(/(\*\*[^*]+\*\*)/g)
    .map((part, i) =>
      part.startsWith("**") && part.endsWith("**")
        ? <strong key={i}>{part.slice(2, -2)}</strong>
        : part
    );
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", content: WELCOME }]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [saved, setSaved] = useState(false);
  const bodyRef = useRef(null);
  const sessionId = useRef(getSessionId());

  useEffect(() => {
    if (open && bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/chat`, {
        session_id: sessionId.current,
        message: text,
      });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
      if (data.lead_saved && !saved) {
        setSaved(true);
        track("generate_lead", { form: "ai_chat" });
        track("ai_assistant_lead_submitted", { page: window.location.pathname });
      }
    } catch (e) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            "Sorry — I had trouble reaching our system. Please call Orlando directly at (678) 356-7281 or use the free estimate form.",
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        data-testid="chat-toggle"
        onClick={() => setOpen((s) => !s)}
        aria-label={open ? "Close chat" : "Open chat"}
        className="fixed bottom-24 right-4 md:bottom-6 md:right-6 z-50 w-14 h-14 rounded-full bg-[#99582A] hover:bg-[#111] text-white shadow-2xl flex items-center justify-center transition-colors"
      >
        {open ? <X size={22} /> : <MessageSquare size={22} />}
      </button>

      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-40 right-4 md:bottom-24 md:right-6 z-50 w-[calc(100vw-2rem)] max-w-md h-[70vh] max-h-[600px] bg-white border border-[#E7E2D9] shadow-2xl flex flex-col"
        >
          <div className="p-4 border-b border-[#E7E2D9] flex items-center gap-3 bg-[#111] text-white">
            <div className="w-9 h-9 bg-[#C27A3E] rounded-full flex items-center justify-center font-display font-bold">
              Z
            </div>
            <div className="flex-1">
              <p className="font-display text-sm font-semibold">Zarco's Decks Assistant</p>
              <p className="text-[10px] uppercase tracking-widest text-white/60">
                Answers questions · books free estimates
              </p>
            </div>
          </div>

          <div ref={bodyRef} data-testid="chat-body" className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#FBF8F3]">
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`chat-msg-${m.role}`}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] px-4 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${
                    m.role === "user"
                      ? "bg-[#111] text-white rounded-tl-2xl rounded-bl-2xl rounded-tr-2xl"
                      : "bg-white text-[#111] border border-[#E7E2D9] rounded-tr-2xl rounded-br-2xl rounded-tl-2xl"
                  }`}
                >
                  {renderBubble(m.content)}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="max-w-[85%] px-4 py-2.5 text-sm bg-white text-[#4A4A4A] border border-[#E7E2D9] rounded-2xl inline-flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin" /> Thinking…
                </div>
              </div>
            )}
            {saved && (
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#99582A] text-center pt-2">
                ✓ Your details were sent to Orlando
              </div>
            )}
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
            className="p-3 border-t border-[#E7E2D9] flex gap-2 bg-white"
          >
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about your project…"
              data-testid="chat-input"
              className="flex-1 border border-[#E7E2D9] px-3 py-2.5 text-sm focus:outline-none focus:border-[#99582A]"
              maxLength={2500}
              disabled={sending}
            />
            <button
              type="submit"
              disabled={sending || !input.trim()}
              data-testid="chat-send"
              className="bg-[#111] hover:bg-[#99582A] text-white px-4 disabled:opacity-40 transition-colors"
            >
              <Send size={16} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
